import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '@/lib/api/client';
import { ORGANIZATIONS_PAGE_SIZE, type OrganizationsSearch } from './search';
import { statusToApi, type OrganizationTab } from './vocab';

export type AdminOrganization = Schemas['AdminOrganizationDto'];
export type OrganizationCounts = Schemas['OrganizationStatusCountsDto'];

export const organizationKeys = {
  all: ['organizations'] as const,
  counts: (keyword: string | undefined) =>
    [...organizationKeys.all, 'counts', keyword ?? ''] as const,
  list: (search: OrganizationsSearch) => [...organizationKeys.all, 'list', search] as const,
  byId: (ids: readonly string[]) => [...organizationKeys.all, 'by-id', ids] as const,
};

/** How many organizations have each status. With no keyword it's also the nav's pending approvals. */
export const organizationCountsQuery = (keyword?: string) =>
  queryOptions({
    queryKey: organizationKeys.counts(keyword),
    queryFn: () =>
      unwrap(api.GET('/api/organizations/admin/counts', { params: { query: { keyword } } })),
    placeholderData: keepPreviousData,
  });

/** One status's organizations, oldest first (the API's order). */
export const organizationsQuery = (search: OrganizationsSearch) =>
  queryOptions({
    queryKey: organizationKeys.list(search),
    queryFn: () =>
      unwrap(
        api.GET('/api/organizations/admin', {
          params: {
            query: {
              status: statusToApi(search.status),
              keyword: search.q,
              pageNumber: search.page,
              pageSize: ORGANIZATIONS_PAGE_SIZE,
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

/** The organizations behind these ids, by id; pass them sorted and unique, at most 100. */
export const organizationsByIdQuery = (ids: readonly string[]) =>
  queryOptions({
    queryKey: organizationKeys.byId(ids),
    queryFn: async () => {
      const found = ids.length
        ? await unwrap(
            api.GET('/api/organizations/admin', {
              params: { query: { id: [...ids], pageSize: 100 } },
            }),
          )
        : [];
      return new Map(found.map((o) => [o.id, o]));
    },
    staleTime: 60_000,
  });

export function useSetOrganizationStatus() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, to }: { id: string; to: OrganizationTab }) =>
      unwrap(
        api.PATCH('/api/organizations/{organizationId}/status', {
          params: { path: { organizationId: id } },
          body: { status: statusToApi(to) },
        }),
      ),
    onSettled: () => queryClient.invalidateQueries({ queryKey: organizationKeys.all }),
  });
}
